<template>
  <div class="user-menu-root">
  <v-menu
    offset-y
    close-on-content-click
    v-model="menu"
    transition="slide-y-transition"
    nudge-bottom="6"
  >
    <template #activator="{ props }">
      <v-avatar
        v-bind="props"
        class="user-menu-avatar"
        size="36"
        @click.stop="menu = !menu"
      >
        <img :src="userImage" alt="User Avatar" />
      </v-avatar>
    </template>

    <v-list class="user-menu-list">
      <!-- Header com identidade do usuário -->
      <v-list-item class="user-menu-header">
        <v-list-item-avatar size="44" class="user-menu-header-avatar mr-3">
          <img :src="userImage" alt="User Avatar" />
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title class="user-menu-name">
            {{ userName }}
          </v-list-item-title>
          <v-list-item-subtitle class="user-menu-email">
            {{ userEmail }}
          </v-list-item-subtitle>
          <v-list-item-subtitle class="user-menu-role">
            <v-icon x-small color="primary" class="mr-1">
              mdi-shield-account-outline
            </v-icon>
            {{ userRoleLabel }}
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>

      <v-divider></v-divider>

      <!-- Seção: Agenda -->
      <div v-if="canSeeClinicAgenda || canSeeDoctorAgenda" class="menu-section-header">
        <v-icon small color="primary" class="mr-2">mdi-calendar</v-icon>
        <span>Agenda</span>
      </div>

      <v-list-item
        v-if="canSeeClinicAgenda"
        :to="{ path: '/calendar-page', query: { mode: 'clinic' } }"
        :class="{ 'selected-item': isSelected('clinic') }"
        class="menu-item"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-icon small>mdi-hospital-building</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Agenda Clínica</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canSeeDoctorAgenda"
        :to="{ path: '/calendar-page', query: { mode: 'doctor' } }"
        :class="{ 'selected-item': isSelected('doctor') }"
        class="menu-item"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-icon small>mdi-stethoscope</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Agenda Médico</v-list-item-title>
      </v-list-item>

      <v-divider v-if="showPatientsOrClinics"></v-divider>

      <!-- Seção: Gestão -->
      <div v-if="showPatientsOrClinics" class="menu-section-header">
        <v-icon small color="primary" class="mr-2">mdi-view-dashboard</v-icon>
        <span>Gestão</span>
      </div>

      <v-list-item
        v-if="canSeePatients"
        to="/patients-page"
        :class="{ 'selected-item': $route.path === '/patients-page' }"
        class="menu-item"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-icon small>mdi-account-group</v-icon>
        </v-list-item-icon>
        <v-list-item-title>
          {{ userType === "assistant" ? "Pacientes" : "Meus Pacientes" }}
        </v-list-item-title>
      </v-list-item>

      <!-- Recados (médico e assistente) — abre a janela de recados (widget global) -->
      <v-list-item
        v-if="canSeeRecados"
        class="menu-item"
        @click="openNotes"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-badge
            :value="notesPendingCount > 0"
            :content="notesPendingCount"
            color="red"
            overlap
            offset-x="8"
            offset-y="8"
          >
            <v-icon small>mdi-message-text-outline</v-icon>
          </v-badge>
        </v-list-item-icon>
        <v-list-item-title>Recados</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canSeeClinics"
        to="/my-clinics-page"
        :class="{ 'selected-item': $route.path === '/my-clinics-page' }"
        class="menu-item"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-icon small>mdi-domain</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Minhas Clínicas</v-list-item-title>
      </v-list-item>

      <v-list-item
        v-if="canSeeAnamneses"
        to="/my-anamneses-page"
        :class="{ 'selected-item': $route.path === '/my-anamneses-page' }"
        class="menu-item"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-icon small>mdi-clipboard-text-multiple</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Minhas Anamneses</v-list-item-title>
      </v-list-item>

      <!-- salvia-805 — pesquisa de CRM/médicos no CNES (ferramenta do comercial).
           Mesma visibilidade do gate do backend (role:doctor) e da meta do router
           (needsToBeADoctor): se aparecesse pro assistente, o clique daria 403. -->
      <v-list-item
        v-if="canSeeCnesSearch"
        to="/cnes-search"
        :class="{ 'selected-item': $route.path === '/cnes-search' }"
        class="menu-item"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-icon small>mdi-card-account-details-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Pesquisa de médicos</v-list-item-title>
      </v-list-item>

      <v-divider></v-divider>

      <!-- Seção: Conta -->
      <div class="menu-section-header">
        <v-icon small color="primary" class="mr-2">mdi-account-cog</v-icon>
        <span>Conta</span>
      </div>

      <v-list-item
        to="/user-profile"
        :class="{ 'selected-item': $route.path === '/user-profile' }"
        class="menu-item"
      >
        <v-list-item-icon class="menu-item-icon">
          <v-icon small>mdi-account-circle-outline</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Perfil</v-list-item-title>
      </v-list-item>

      <v-list-item @click="logout" class="menu-item menu-item--danger">
        <v-list-item-icon class="menu-item-icon">
          <v-icon small color="#d32f2f">mdi-logout</v-icon>
        </v-list-item-icon>
        <v-list-item-title>Sair</v-list-item-title>
      </v-list-item>
    </v-list>
  </v-menu>
  </div>
</template>

<script>
import http from "@/services/http";
import { useSnackbar } from "@/composables/useSnackbar";
import { getInbox } from "@/services/assistantNoteService";

export default {
  name: "UserMenu",

  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },

  data() {
    return {
      menu: false,
      loggingOut: false,
      fallbackAvatar: require("@/assets/images/profile.jpeg"),
      notesPendingCount: 0,
    };
  },

  computed: {
    user() {
      return this.$store.getters["auth/user"];
    },
    userType() {
      return this.$store.getters["auth/userType"];
    },

    // ✅ fonte única de verdade: Vuex
    profileImageUrl() {
      return this.$store.getters["auth/profileImageUrl"];
    },

    userName() {
      return this.user?.name || "";
    },
    userEmail() {
      return this.user?.email || "";
    },

    // ✅ sempre lê do Vuex; se não tiver ainda, usa fallback local
    userImage() {
      return this.profileImageUrl || this.fallbackAvatar;
    },

    userRoleLabel() {
      switch (this.userType) {
        case "assistant":
          return "Assistente da Clínica";
        case "doctor_clinic":
          return "Médico (vinculado à clínica)";
        case "doctor_solo":
          return "Médico (independente)";
        default:
          return "Usuário";
      }
    },

    showPatientsOrClinics() {
      return ["assistant", "doctor_clinic", "doctor_solo"].includes(
        this.userType,
      );
    },

    canSeeClinicAgenda() {
      return this.userType === "assistant" || this.userType === "doctor_clinic";
    },
    canSeeDoctorAgenda() {
      return (
        this.userType === "doctor_clinic" || this.userType === "doctor_solo"
      );
    },
    canSeePatients() {
      return ["assistant", "doctor_clinic", "doctor_solo"].includes(
        this.userType,
      );
    },
    canSeeClinics() {
      return ["assistant", "doctor_clinic", "doctor_solo"].includes(
        this.userType,
      );
    },
    canSeeAnamneses() {
      return ["doctor_clinic", "doctor_solo"].includes(this.userType);
    },
    canSeeCnesSearch() {
      return ["doctor_clinic", "doctor_solo"].includes(this.userType);
    },
    canSeeRecados() {
      return ["assistant", "doctor_clinic"].includes(this.userType);
    },
  },

  watch: {
    // "procurar assim que abrir o menu sempre"
    menu(open) {
      if (open && this.canSeeRecados) this.fetchNotesCount();
    },
  },

  async mounted() {
    // ✅ garante user + avatar no Vuex
    try {
      if (!this.$store.getters["auth/isAuthenticated"]) return;

      await this.$store.dispatch("auth/ensureAuth");

      // ✅ se por algum motivo o avatar ainda não foi carregado, carrega agora
      if (!this.$store.getters["auth/profileImageUrl"]) {
        await this.$store.dispatch("auth/fetchProfileImage");
      }

      // Bolinha de recados pendentes (médico e assistente), já no login.
      if (this.canSeeRecados) {
        this.fetchNotesCount();
      }
    } catch {
      // deixa a UI seguir
    }

    // O widget global é a fonte de verdade da contagem; sincroniza a bolinha.
    this.$root.$on("recados-pending", this.setNotesPendingCount);
  },

  beforeDestroy() {
    this.$root.$off("recados-pending", this.setNotesPendingCount);
  },

  methods: {
    openNotes() {
      // Abre a janela de recados hospedada no widget global.
      this.$root.$emit("open-recados");
    },

    setNotesPendingCount(count) {
      this.notesPendingCount = count || 0;
    },

    async fetchNotesCount() {
      try {
        const res = await getInbox();
        this.notesPendingCount = res.pendingCount;
      } catch (e) {
        this.notesPendingCount = 0;
      }
    },

    isSelected(mode) {
      return (
        this.$route.path === "/calendar-page" &&
        (this.$route.query.mode || "doctor") === mode
      );
    },

    async logout() {
      if (this.loggingOut) return;
      this.loggingOut = true;

      try {
        await http.post("/api/logout");
        this.$store.dispatch("auth/logout");

        this.success("Logout realizado com sucesso");
        this.$router.push({ name: "LandingPage" });
      } catch (err) {
        console.error("Erro ao realizar logout:", err);

        this.$store.dispatch("auth/logout");
        this.erroDeRequisicao(err, 
          "Não foi possível finalizar o logout no servidor. Você foi desconectado localmente.",
        );
        this.$router.push({ name: "LandingPage" });
      } finally {
        this.loggingOut = false;
        this.menu = false;
      }
    },
  },
};
</script>

<style scoped>
/* Wrapper (agrupa o menu + o dialog de recados num único root) */
.user-menu-root {
  display: inline-flex;
  align-items: center;
}

/* ═══ Avatar trigger ═══ */
.user-menu-avatar {
  cursor: pointer;
  border: 2px solid var(--ds-blue-100);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.user-menu-avatar:hover {
  border-color: var(--ds-primary);
  box-shadow: 0 2px 8px rgba(31, 77, 63, 0.2);
}

/* ═══ Dropdown list ═══ */
.user-menu-list {
  min-width: 280px;
  border-radius: 12px !important;
  padding: 6px 0 4px 0 !important;
}

.user-menu-list :deep(.v-list-item) {
  min-height: 40px !important;
}

/* ═══ Header com identidade ═══ */
.user-menu-header {
  padding: 14px 16px !important;
  background: linear-gradient(180deg, var(--ds-blue-50) 0%, var(--ds-surface-white) 100%);
}

.user-menu-header-avatar {
  border: 2px solid var(--ds-blue-100);
  box-shadow: 0 2px 6px rgba(31, 77, 63, 0.1);
}

.user-menu-name {
  font-size: 14px !important;
  font-weight: 700 !important;
  color: var(--ds-text-strong) !important;
  letter-spacing: -0.2px;
}

.user-menu-email {
  font-size: 12px !important;
  color: var(--ds-text-soft) !important;
  margin-top: 2px;
}

.user-menu-role {
  font-size: 10px !important;
  color: var(--ds-primary) !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  font-weight: 700 !important;
  display: flex !important;
  align-items: center !important;
  margin-top: 4px;
}

/* ═══ Section headers ═══ */
.menu-section-header {
  display: flex;
  align-items: center;
  padding: 8px 16px 4px 16px;
  font-size: 10px;
  font-weight: 700;
  color: var(--ds-primary);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

/* ═══ Items ═══ */
.menu-item {
  padding-left: 16px !important;
  padding-right: 12px !important;
  transition: background-color 0.15s ease, padding-left 0.15s ease;
}

.menu-item:hover {
  background-color: var(--ds-blue-bg) !important;
  padding-left: 20px !important;
}

.menu-item :deep(.v-list-item__title) {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: var(--ds-text-700) !important;
}

.menu-item-icon {
  min-width: 28px !important;
  margin-right: 8px !important;
  align-self: center !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.menu-item-icon :deep(.v-icon) {
  color: var(--ds-text-500) !important;
}

/* Selected item — barra lateral azul + bg suave (mesmo padrão do drawer) */
.selected-item {
  background-color: var(--ds-blue-100) !important;
  border-left: 3px solid var(--ds-primary);
  padding-left: 13px !important;
}

.selected-item :deep(.v-list-item__title) {
  color: var(--ds-primary) !important;
  font-weight: 700 !important;
}

.selected-item .menu-item-icon :deep(.v-icon) {
  color: var(--ds-primary) !important;
}

.selected-item:hover {
  padding-left: 17px !important;
}

/* ═══ Item de logout em destaque vermelho suave ═══ */
.menu-item--danger:hover {
  background-color: #ffebee !important;
}

.menu-item--danger :deep(.v-list-item__title) {
  color: var(--ds-danger-light) !important;
}
</style>
